import { Monitor, Moon, Sun } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '~/context/ThemeContext';
import type { Theme } from '~/context/ThemeContext';
import { useTranslate } from '@tolgee/react';

const OPTIONS: Array<{
  value: Theme;
  Icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
  fallback: string;
}> = [
  {
    value: 'system',
    Icon: Monitor,
    labelKey: 'theme_system',
    fallback: 'System',
  },
  { value: 'light', Icon: Sun, labelKey: 'theme_light', fallback: 'Hell' },
  { value: 'dark', Icon: Moon, labelKey: 'theme_dark', fallback: 'Dunkel' },
];

interface ThemeToggleProps {
  readonly className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslate();

  return (
    <div
      className={clsx(
        'inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-1 gap-1',
        className,
      )}
      role="radiogroup"
      aria-label={t('theme_toggle_label', 'Erscheinungsbild')}
    >
      {OPTIONS.map(({ value, Icon, labelKey, fallback }) => {
        const isActive = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => setTheme(value)}
            className={clsx(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
              isActive
                ? 'bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 dark:text-white shadow-sm dark:shadow-gray-900/50 ring-1 ring-gray-200 dark:ring-gray-700 dark:ring-gray-600'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-300 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-gray-800 dark:bg-gray-800/60 dark:hover:bg-gray-700/50',
            )}
          >
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span>{t(labelKey, fallback)}</span>
          </button>
        );
      })}
    </div>
  );
}
